import { Icon } from '@iconify/react'
import { batch } from '@legendapp/state'
import { Memo, observer } from '@legendapp/state/react'
import { ColumnDef, flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, RowSelectionState, SortingState, Updater, useReactTable } from '@tanstack/react-table'
import { useState } from 'react'

import { CustomizeDropdownMenuItemProps, DropdownMenuComponent, Input, Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink } from '~in-app/components'

import CheckboxComponent from '../Checkbox'
import DatePicker from '../Datepicker'
import Dropdown from '../Dropdown'
import { FilterDropDown } from '../FilterDropDown'
import MultiSelectDropdown from '../MultiSelectDropdown'
import { useTableContext } from './TableContext'

const TanstackTable = observer(() => {
  const layoutContext = useTableContext()
  const [sorting, setSorting] = useState<SortingState>(layoutContext.state$?.sort.get() ?? [])
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  })

  const tableData = useReactTable({
    defaultColumn: {
      size: 200,
      minSize: 50,
      maxSize: 500,
    },
    initialState: {
      pagination: { pageSize: layoutContext.state$?.pageSize.get() ?? 20, pageIndex: layoutContext.state$.pagination.get() },
      columnVisibility: Object.fromEntries(
        (layoutContext.state$?.hideColumns?.get() ?? []).map(column => [column, false]),
      ),
    },
    data: layoutContext.state$?.data.get(),
    columns: layoutContext.state$?.columns.get() as ColumnDef<unknown, any>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    rowCount: layoutContext.state$?.totalItem.get(),
    pageCount: layoutContext.state$?.totalPage.get(),
    enableMultiSort: true,
    isMultiSortEvent: () => true,
    manualPagination: true,
    manualSorting: true,
    getRowId: (row: any) => row.id,
    state: {
      sorting,
      pagination: { pageSize: layoutContext.state$?.pageSize.get() ?? 20, pageIndex: layoutContext.state$.pagination.get() ?? 0 },
      rowSelection: layoutContext.state$.selectedRows.get()?.reduce((acc: Record<string, boolean>, row: any) => {
        acc[row.id] = true
        return acc
      }, {}) ?? {},
    },
    onPaginationChange: setPagination,
    onSortingChange: (value) => {
      layoutContext.state$.setSort(value)
      setSorting(value)
    },
    enableRowSelection: layoutContext.state$?.enableRowSelection?.get() ?? false,
    enableMultiRowSelection: true,
    onRowSelectionChange: (updaterOrValue: Updater<RowSelectionState>) => {
      const updatedSelection = typeof updaterOrValue === 'function'
        ? updaterOrValue(tableData.getState().rowSelection)
        : updaterOrValue
      const selectedRows = Object.keys(updatedSelection)
        .filter(key => updatedSelection[key])
        .map(key => tableData.getRowModel().rowsById[key]?.original)

      layoutContext.state$.selectedRows.set(selectedRows)
    },
  })

  layoutContext.state$.assign({ table: tableData })

  const currentPage = (layoutContext.state$.pagination.get() ?? 0) + 1
  const totalPage = tableData.getPageCount() === 0 ? 1 : tableData.getPageCount()
  // const pageSize = tableData.getState().pagination.pageSize
  const onApplyFunc = layoutContext.state$.onApply.get()
  // Define default column visibility
  const defaultColumnVisibility = tableData.getAllColumns().reduce((acc, column) => {
    acc[column.id] = true // Initially, all columns are visible
    return acc
  }, {} as Record<string, boolean>)

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(defaultColumnVisibility)

  // Create dropdown items based on table headers
  const dropdownItems: CustomizeDropdownMenuItemProps[] = layoutContext.state$?.table.getHeaderGroups().flatMap(headerGroup =>
    headerGroup.headers.filter(head => head.id !== 'action').map(header => (
      {
        label: flexRender(header.column.columnDef.header, header.getContext()),
        isCheckbox: true,
        checked: columnVisibility[header.id],
        onCheckedChange: () => toggleColumnVisibility(header.id),
      })),
  )

  const toggleColumnVisibility = (columnId: string) => {
    const visibleColumnsCount = Object.values(columnVisibility).filter(Boolean).length

    if (visibleColumnsCount === 1 && columnVisibility[columnId]) {
      return // Prevent hiding the last visible column
    }

    setColumnVisibility(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }))
  }

  // Reset function to restore defaults
  const handleReset = () => {
    setColumnVisibility(defaultColumnVisibility)
  }

  const handleToggleSort = (header: any) => {
    const ctx = header.getContext()
    const isSorted = header.column.getIsSorted()
    const isSortDescFirst = !!header.column.columnDef.sortDescFirst || !!ctx.table.options.sortDescFirst
    const isEnableSortingRemoval = !!ctx.table.options.enableSortingRemoval

    if (!isSorted) {
      layoutContext.state$.setSort({ id: header.id, sortDirection: isSortDescFirst ? 'desc' : 'asc' })

      header.column.toggleSorting(isSortDescFirst)
    }

    if (isSorted === 'asc') {
      if (isSortDescFirst && isEnableSortingRemoval) {
        header.column.clearSorting()
        layoutContext.state$.setSort({ id: header.id, sortDirection: null })
      }
      else {
        header.column.toggleSorting(true)
        layoutContext.state$.setSort({ id: header.id, sortDirection: 'desc' })
      }
    }

    if (isSorted === 'desc') {
      if (!isSortDescFirst && isEnableSortingRemoval) {
        header.column.clearSorting()
        layoutContext.state$.setSort({ id: header.id, sortDirection: null })
      }
      else {
        header.column.toggleSorting(false)
        layoutContext.state$.setSort({ id: header.id, sortDirection: 'asc' })
      }
    }
  }

  // Classes for pagination buttons
  const selectedPageClass = 'flex items-center justify-center px-3 h-8 text-blue-600 border border-gray-300 bg-blue-50 hover:bg-blue-200 hover:text-blue-700 hover:font-bold dark:border-gray-700 dark:bg-gray-700 dark:text-white'
  const unstatePageClass = 'flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'

  // Function to handle select all
  const handleSelectAll = (checked: boolean) => {
    tableData.toggleAllRowsSelected(checked)
    const selectedRows = checked
      ? tableData.getRowModel().rows.map(row => row.original)
      : []
    layoutContext.state$.selectedRows.set(selectedRows)
  }

  return (
    <div className="relative">
      {/* Dropdown Menu for Column Visibility */}
      <div className="flex justify-end gap-2 p-2">
        <div>
          <DropdownMenuComponent
            preventClose
            buttonLabel="Columns"
            className="rounded-md bg-transparent p-2 hover:opacity-100"
            items={dropdownItems}
            buttonIcon={
              (
                <Icon
                  icon="lucide:columns-3"
                />
              )
            }
            onReset={handleReset} // Ensure reset function is passed here
          />
        </div>

        {
          layoutContext.state$?.filters.get() && (
            <div>
              <FilterDropDown
                buttonLabel="Filters"
                onApply={() => onApplyFunc?.(layoutContext.state$.filters.get())}
                onReset={
                  () => {
                    batch(() => {
                      layoutContext.state$.filters.map((filter) => {
                        filter.result.delete()
                      })
                    })
                  }
                }
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Grid container for layout */}
                  <Memo>
                    {
                      () =>
                        layoutContext.state$.filters.get()!.map((filter, index) => {
                          const { label, type, value, result, options, isSplitRow, isMultiSelect, id } = filter // Destructure filter props
                          return (
                            <div
                              key={index}
                              className={`mb-4 ${!isSplitRow ? 'col-span-2' : ''}`}
                            >
                              {/* Text Input */}
                              {
                                type === 'text' && (
                                  <div className="col-span-1">

                                    <label className="block text-sm font-medium text-gray-700">{label}</label>

                                    <Input
                                      className="mt-1 block w-full"
                                      type="text"
                                      value={result && (result === value) ? value : result}
                                      onChange={
                                        (e) => {
                                          layoutContext.state$.filters[index].result.set(e.target.value)
                                        }
                                      }
                                    />
                                  </div>
                                )
                              }

                              {/* Date Picker */}
                              {
                                type === 'date' && (
                                  <div className="col-span-1">
                                    <label className="block text-sm font-medium text-gray-700">{label}</label>

                                    <DatePicker
                                      className="mt-1 block w-full"
                                      selectedDate={result ? result : value instanceof Date ? value : new Date()} // Ensure a valid start date
                                      onDateChange={
                                        (e) => {
                                          layoutContext.state$.filters[index].result.set(e)
                                        }
                                      }
                                    />
                                  </div>
                                )
                              }

                              {/* Dropdown */}
                              {
                                type === 'dropdown' && options && (
                                  <div className="col-span-1">
                                    <label className="block text-sm font-medium text-gray-700">{label}</label>

                                    {
                                      isMultiSelect
                                        ? (
                                            <MultiSelectDropdown
                                              id={id}
                                              options={options ?? []}
                                              placeholder="Select at least an option"
                                              value={layoutContext.state$.filters[index].result.get() ?? []}
                                              onChange={selected => layoutContext.state$.filters[index].result.set(selected)}
                                            />
                                          )
                                        : (
                                            <Dropdown
                                              id={id}
                                              label=""
                                              options={options}
                                              value={result ? result : value}
                                              onValueChange={
                                                (e) => {
                                                  layoutContext.state$.filters[index].result.set(e)
                                                }
                                              }
                                            />
                                          )
                                    }

                                  </div>
                                )
                              }

                              {/* Checkbox */}
                              {
                                type === 'checkbox' && (
                                  <div className="col-span-1 flex items-center space-x-2">
                                    <span className="block text-sm font-medium text-gray-700">{label}</span>

                                    <CheckboxComponent
                                      value={typeof result === 'boolean' ? result : value} // Provide the current filter value
                                      onChange={
                                        (e) => {
                                          layoutContext.state$.filters[index].result.set(e)
                                        }
                                      }
                                    />
                                  </div>
                                )
                              }
                            </div>
                          )
                        })
                    }
                  </Memo>

                </div>
              </FilterDropDown>

            </div>
          )
        }
      </div>

      <div style={{ overflowX: 'auto', width: '100%', overflowY: 'auto', maxHeight: '65vh' }}>

        <table
          className="border border-inherit text-left text-sm text-gray-500 dark:text-gray-400 sm:rounded-lg"
          id={layoutContext.state$.id.get()}
          style={{ tableLayout: 'fixed', overflowX: 'auto', width: '100%', overflowY: 'auto', maxHeight: '65vh' }}
        >
          {/* THead */}
          <thead
            className="border-x border-slate-100 bg-gray-100 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400"
            style={{ position: 'sticky', top: 0, zIndex: 10 }}
          >
            <tr>
              {/* Update select all checkbox column styling */}
              {
                layoutContext.state$?.enableRowSelection?.get() && (
                  <th className="w-[50px] border-x border-slate-100 px-6 py-3">
                    <div className="flex items-center justify-center">
                      <CheckboxComponent
                        value={tableData.getIsAllRowsSelected() && tableData.getRowModel().rows.length > 0}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                )
              }

              {
                tableData?.getHeaderGroups().map(headerGroup =>
                  headerGroup.headers.map(header =>
                    columnVisibility[header.id]
                      ? (
                          <th
                            key={header.id}
                            className="break-all border-x border-slate-100 px-6 py-3"
                            scope="col"
                            style={{ width: `${header.getSize()}px` }} // Adjust styles here
                          >
                            {
                              !header.isPlaceholder && (
                                <div className="flex">
                                  <div
                                    className="flex cursor-pointer items-center"
                                    onClick={header.column.getToggleSortingHandler()}

                                  >
                                    {flexRender(header.column.columnDef.header, header.getContext())}

                                    {
                                      {
                                        asc: (
                                          <Icon
                                            className="ml-1"
                                            height="12"
                                            icon="fa-solid:sort-up"
                                            width="12"
                                          />
                                        ),
                                        desc: (
                                          <Icon
                                            className="ml-1"
                                            height="12"
                                            icon="fa-solid:sort-down"
                                            width="12"
                                          />
                                        ),
                                      }[header.column.getIsSorted() as string] ?? null
                                    }
                                  </div>
                                </div>
                              )
                            }
                          </th>
                        )
                      : null,
                  ),
                )
              }
            </tr>
          </thead>

          {/* TBody */}
          <tbody>
            {
              tableData.getRowModel().rows.length === 0
                ? (
                    <tr>
                      <td
                        className="py-4 text-center"

                      >
                        <div className="p-2">
                          <p className="text-gray-500 dark:text-gray-400">No data available in the table</p>
                        </div>
                      </td>
                    </tr>
                  )
                : (
                    tableData.getRowModel().rows.map(row => (
                      <tr
                        key={row.id}
                        className="border-b bg-white dark:border-gray-700 dark:bg-gray-800"
                      >
                        {/* Update row checkbox column styling */}
                        {
                          layoutContext.state$?.enableRowSelection?.get() && (
                            <td className="w-[50px] border-x border-slate-100 p-2">
                              <div className="flex items-center justify-center">
                                <CheckboxComponent
                                  value={row.getIsSelected()}
                                  onChange={
                                    (checked: boolean) => {
                                      row.toggleSelected(checked)
                                      const selectedRows = checked
                                        ? [...(tableData.getSelectedRowModel().rows.map(r => r.original))]
                                        : tableData.getSelectedRowModel().rows
                                            .filter(r => r.id !== row.id)
                                            .map(r => r.original)
                                      layoutContext.state$?.onRowSelectionChange?.(selectedRows)
                                    }
                                  }
                                />
                              </div>
                            </td>
                          )
                        }

                        {
                          row.getVisibleCells().map(cell =>
                            columnVisibility[cell.column.id]
                              ? (
                                  <td
                                    key={cell.id}
                                    className="border-x border-slate-100 p-2"
                                    style={{ overflow: 'hidden', wordWrap: 'break-word', maxWidth: '200px', maxHeight: '50px', overflowY: 'auto' }} // Adjust styles here
                                  >
                                    {
                                      cell.column.columnDef.cell
                                        ? flexRender(cell.column.columnDef.cell, cell.getContext())
                                        : cell.getValue() !== null && cell.getValue() !== undefined
                                          ? String(cell.getValue())
                                          : '-'
                                    }
                                  </td>
                                )
                              : null,
                          )
                        }
                      </tr>
                    ))
                  )
            }
          </tbody>
        </table>

      </div>

      <Pagination
        aria-label="Table navigation"
        className="flex w-full items-end justify-between p-2"
      >
        <span className="flex self-center text-sm font-normal text-gray-500 dark:text-gray-400">
          Page
          {' '}
          {currentPage}
          {' '}
          of
          {' '}
          {totalPage}
          {' '}
          - Showing

          <select
            className="ml-2 border border-gray-300 bg-transparent px-2"
            value={tableData.getState().pagination.pageSize}
            onChange={
              (e) => {
                tableData.setPageSize(Number(e.target.value))
                tableData.setPageIndex(0)
                layoutContext.state$.setPageSize(Number(e.target.value))
                layoutContext.state$.setPagination(0)
              }
            }
          >
            {
              [10, 20, 30, 40, 50].map(pageSize => (
                <option
                  key={pageSize}
                  value={pageSize}
                >
                  {pageSize}
                </option>
              ))
            }
          </select>
        </span>

        <PaginationContent>

          <PaginationItem>
            <PaginationLink
              className="flex h-8 items-center justify-center border border-gray-300 bg-white px-3 leading-tight text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              onClick={
                () => {
                  tableData.setPageIndex(0)
                  layoutContext.state$.setPagination(0)
                }
              }
            >
              <Icon icon="lucide:arrow-left-to-line" />
            </PaginationLink>
          </PaginationItem>

          <PaginationItem>
            <PaginationLink
              className="ml-0 flex h-8 items-center justify-center rounded-l-lg border border-gray-300 bg-white px-3 leading-tight text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              onClick={
                () => {
                  if (tableData.getCanPreviousPage()) {
                    tableData.previousPage()
                    tableData.setPageIndex(currentPage - 2)
                    layoutContext.state$.setPagination(currentPage - 2)
                  }
                }
              }
            >
              Previous
            </PaginationLink>
          </PaginationItem>

          {
            Array.from({ length: totalPage }, (_, i) => {
              const pageNumber = i + 1

              if (totalPage <= 6) {
                // Show all pages if totalPage is 6 or less
                return (
                  <PaginationItem key={pageNumber}>
                    <PaginationLink
                      className={pageNumber === currentPage ? selectedPageClass : unstatePageClass}
                      isActive={pageNumber === currentPage}
                      onClick={
                        () => {
                          tableData.setPageIndex(i)
                          layoutContext.state$.setPagination(i)
                        }
                      }
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                )
              }
              else {
                // Logic for more than 6 pages
                if (
                  pageNumber === 1 // Always show first page
                  || pageNumber === 2 // Always show second page
                  || pageNumber === totalPage // Always show last page
                  || pageNumber === totalPage - 1 // Always show second to last page
                  || (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1) // Show current, previous, and next page
                ) {
                  return (
                    <PaginationItem key={pageNumber}>
                      <PaginationLink
                        className={pageNumber === currentPage ? selectedPageClass : unstatePageClass}
                        isActive={pageNumber === currentPage}
                        onClick={
                          () => {
                            tableData.setPageIndex(i)
                            layoutContext.state$.setPagination(i)
                          }
                        }
                      >
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  )
                }

                // Ellipsis logic
                if (pageNumber === 3 && currentPage > 4) {
                  // Ellipsis after page 2
                  return (
                    <PaginationItem key="ellipsis-start">
                      <PaginationEllipsis />
                    </PaginationItem>
                  )
                }

                if (pageNumber === totalPage - 2 && currentPage < totalPage - 3) {
                  // Ellipsis before last two pages
                  return (
                    <PaginationItem key="ellipsis-end">
                      <PaginationEllipsis />
                    </PaginationItem>
                  )
                }
              }

              // Do not render other pages
              return null
            })
          }

          <PaginationItem>
            <PaginationLink
              aria-disabled={!tableData.getCanNextPage()}
              className={`flex h-8 items-center justify-center rounded-r-lg border border-gray-300 bg-white px-3 leading-tight text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white ${!tableData.getCanNextPage() ? 'bg-gray-200' : ''}`}
              onClick={
                () => {
                  if (tableData.getCanNextPage()) {
                    tableData.nextPage()
                    tableData.setPageIndex(currentPage)
                    layoutContext.state$.setPagination(currentPage)
                  }
                }
              }
            >
              Next
            </PaginationLink>
          </PaginationItem>

          <PaginationItem>
            <PaginationLink
              aria-disabled={!tableData.getCanNextPage()}
              className="flex h-8 items-center justify-center border border-gray-300 bg-white px-3 leading-tight text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
              onClick={
                () => {
                  if (tableData.getCanNextPage()) {
                    tableData.setPageIndex(totalPage - 1)
                    layoutContext.state$.setPagination(totalPage - 1)
                  }
                }
              }
            >
              <Icon icon="lucide:arrow-right-to-line" />
            </PaginationLink>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  )
})

export { TanstackTable }
